import Components from "slotworks/components/components";
import ClusterResult from "slotworks/model/gameplay/records/results/cluster-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import settingsModel from "slotworks/model/settings/settings-model";
import slotDefinition from "slotworks/model/slot-definition";
import { arrayOfValues } from "slotworks/utils/collection-utils";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import ReelComponent from "../../reel-component";
import AbstractReelTransition from "../abstract-reel-transition";
import ReelSymbolRemover from "./reel-symbol-remover";

export class RemoveTransition extends AbstractReelTransition {

    public static REMOVE_DELAY: number = 500;
    public static REMOVE_DELAY_QUICK_SPIN: number = 300;
    public static REMOVE_DELAY_TURBO_SPIN: number = 150;
    public static AFFECTED_SYMBOLS_DELAY: number = 0;

    protected removers: ReelSymbolRemover[] = [];
    protected cancelGroup: CancelGroup = new CancelGroup();

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]): void {
        super.init(reels, stops, reelset);
        this.reels = reels;
        this.removers = [];

        reels.forEach((reel: ReelComponent, i: number) => {
            const remover: ReelSymbolRemover = new ReelSymbolRemover(reel, stops, reelset);
            this.removers.push(remover);
        });
    }

    public destroy(): void {
        // ..
    }

    public stop(spinRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {
        const removeContracts: Array<() => Contract<void>> = [];
        const clusterResults: ClusterResult[] = spinRecord.getResultsOfType(ClusterResult);
        const turboSpin: boolean = settingsModel.getTurboSpin();
        clusterResults.forEach((result: ClusterResult) => removeContracts.push(() => this.remove(result, quickSpin, turboSpin)));
        return this.cancelGroup.parallel(removeContracts);
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        return new Contract<void>((resolve: (value?: any) => void) => {
            this.cancelGroup.cancel();
            const clusterResults: ClusterResult[] = spinRecord.getResultsOfType(ClusterResult);
            const pointsByClusterResult: Point[][][] = new Array<Point[][]>(clusterResults.length);
            clusterResults.forEach((result: ClusterResult) => {
                pointsByClusterResult.push(this.organisePointsByReel(result.positions));
                pointsByClusterResult.push(this.organisePointsByReel(result.affectedPositions));
            });
            pointsByClusterResult.forEach((symbolsPerReel: Point[][]) => symbolsPerReel.forEach((points: Point[], index: number) => this.removers[index].skip(points)));
            Components.matrix.resetSymbolPositions();
            resolve();
        });
    }

    protected remove(result: ClusterResult, quickSpin: boolean, turboSpin: boolean): Contract<void> {
        const removeContracts: Array<() => Contract<void>> = [];
        const positionsByReel: Point[][] = this.organisePointsByReel(result.positions);
        const delay: number = turboSpin ? RemoveTransition.REMOVE_DELAY_TURBO_SPIN : quickSpin ? RemoveTransition.REMOVE_DELAY_QUICK_SPIN : RemoveTransition.REMOVE_DELAY;
        positionsByReel.forEach((points: Point[], index: number) => {
            removeContracts.push(() => Contract.getDelayedContract(delay, () => this.removers[index].removeSymbols(points)));
        });

        if (result.affectedPositions) {
            const affectedPointsByReel: Point[][] = this.organisePointsByReel(result.affectedPositions);
            const affectedDelay: number = delay + RemoveTransition.AFFECTED_SYMBOLS_DELAY;
            affectedPointsByReel.forEach((points: Point[], index: number) => {
                removeContracts.push(() => Contract.getDelayedContract(affectedDelay, () => this.removers[index].removeSymbols(points)));
            });
        }

        return this.cancelGroup.parallel(removeContracts);
    }

    protected organisePointsByReel(points: Point[]): Point[][] {
        const symbolsPerReel: Point[][] = arrayOfValues<Point[]>(slotDefinition.matrixGrid.length, []);
        points.forEach((point: Point) => symbolsPerReel[point.x].push(point));
        return symbolsPerReel;
    }
}
